/**
 * Desktop Profile (lane D1; INVENTORY-DESKTOP §3.10, prototype template lines
 * 699-777).
 *
 * Reached from the top-nav avatar chip. There is NO `FRIENDS` row here — the
 * nav owns that jump on desktop (§3.10), which is the one piece of mobile's
 * Profile that does not exist in this tree.
 *
 * The identity and password fields live in the ui slice (`pfName`, `pfEmail`,
 * `pfCur`, `pfN1`, `pfN2`) and are pre-filled by `nav('profile')`, so the 96px
 * avatar's sibling caption tracks what you are typing — same binding the
 * prototype used. `profileVM`'s `now` defaults to `Date.now()` and the ui
 * slice's 5 s `tick` re-renders this screen, which re-reads the clock for
 * `CHECKED …`.
 *
 * The one piece of real work this screen does is the avatar crop: `onPickAv()`
 * takes a **data URL** (PORTING-NOTES P2-04), so FileReader → Image →
 * centre-crop → `toDataURL('image/jpeg', 0.85)` happens here.
 *
 * DESKTOP-ONLY (§3.10): IDENTITY sits BESIDE SECURITY, the avatar is 96px with
 * a hover-revealed `UPLOAD / 256 × 256 PX` overlay instead of mobile's permanent
 * `TAP TO UPLOAD` strip, and the panel header strips carry a mono caption.
 *
 * DEVIATIONS
 *  - §9.2 resolved in favour of the copy: the overlay promises `256 × 256 PX`
 *    and the canvas really is 256 (mobile's D-10 already raised it from the
 *    prototype's 128). No contradiction ships.
 *  - §9.8 / D-10: the Steam call-to-action uses `var(--accSoft)`, not the
 *    template's literal `rgba(90,240,150,.06)`, which baked in the green accent
 *    and rendered green under cyan / magenta / amber.
 *  - The button keeps the app's `MANAGE STEAM LINK` (`profileVM`) over the
 *    prototype's `MANAGE`, per §3.10.
 *  - D-01 (inherited): password copy comes from the store
 *    (`PASSWORD_MIN_COPY_PROFILE`); this screen never states a number.
 *  - D0-04: `data-tip` copy is authored UPPERCASE at the call site
 *    (`UPLOAD A PROFILE IMAGE`), because `Tooltip` applies no text-transform.
 */

import type { CSSProperties } from 'react';

import { CharacterPanel } from '../../components/CharacterPanel.tsx';
import { PixelButton } from '../../components/PixelButton.tsx';
import { useQd } from '../../store/index.ts';
import { pfSteamDot, PF_STEAM_CAP } from '../../store/selectors.3b.ts';
import { profileVM } from '../../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';

const FIELD: CSSProperties = {
  background: 'var(--gt-field)',
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text)',
  fontFamily: MONO,
  fontSize: '12px',
  padding: '10px 12px',
  outline: 'none',
};

const LABEL: CSSProperties = { fontFamily: MONO, fontSize: '9.5px', color: 'var(--gt-muted-2)' };

const GROUP: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '6px' };

/** §3.10 panel chrome — shared by IDENTITY, SECURITY and STEAM ACCOUNT. */
const PANEL: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid var(--gt-border)',
  background: 'var(--gt-panel)',
  boxShadow: 'var(--gt-shadow-card)',
};

const PANEL_HEAD: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: '10px',
  background: 'var(--gt-raised)',
  borderBottom: '1px solid var(--gt-border)',
  padding: '12px 16px',
};

const PANEL_TITLE: CSSProperties = {
  fontFamily: 'var(--gt-font-pixel)',
  fontSize: '8px',
  color: 'var(--gt-text)',
};

const PANEL_CAP: CSSProperties = {
  fontFamily: MONO,
  fontSize: '9px',
  letterSpacing: '.05em',
  color: 'var(--gt-muted-2)',
};

const PANEL_BODY: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
  padding: '16px',
};

/** The two side-by-side panels (§1.3 row "Profile"). */
const PANEL_HALF: CSSProperties = { ...PANEL, flex: '1 1 340px', minWidth: '280px' };

/** §9.2 — the overlay copy and this constant must always agree. */
const AVATAR_PX = 256;

/**
 * Prototype `onPickAv` (line 1787) at 256 px (mobile deviation D-10, kept).
 * Centre-crops the largest square the image allows, so a portrait or landscape
 * source never distorts, and hands the store a data URL.
 */
function cropToDataUrl(file: File, done: (dataUrl: string) => void): void {
  const rd = new FileReader();
  rd.onload = () => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = AVATAR_PX;
      c.height = AVATAR_PX;
      const x = c.getContext('2d');
      if (!x) return;
      const sz = Math.min(img.width, img.height);
      x.drawImage(img, (img.width - sz) / 2, (img.height - sz) / 2, sz, sz, 0, 0, AVATAR_PX, AVATAR_PX);
      done(c.toDataURL('image/jpeg', 0.85));
    };
    img.src = String(rd.result);
  };
  rd.readAsDataURL(file);
}

export default function Profile(): JSX.Element {
  const s = useQd();
  const v = profileVM(s);

  return (
    <div
      data-screen-label="Profile"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '22px',
        padding: '30px 26px 40px',
        maxWidth: '860px',
        width: '100%',
        margin: '0 auto',
        boxSizing: 'border-box',
      }}
    >
      {/* PROFILE is MY GAMES' sub-page (DESIGN-CHANGELOG 2026-09-03): the way
          back, `design/GT Crew.dc.html:434`. The header's account menu is the
          way in. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span
          onClick={() => s.nav('mine')}
          className="d-hov-acc-box"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            height: '40px',
            padding: '0 13px',
            border: '1px solid var(--gt-border-2)',
            boxSizing: 'border-box',
            fontFamily: MONO,
            fontSize: '10px',
            letterSpacing: '.06em',
            color: 'var(--gt-text-4)',
            cursor: 'pointer',
          }}
        >
          ◂ MY GAMES
        </span>
      </div>
      <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
        <label
          data-tip="UPLOAD A PROFILE IMAGE"
          style={{
            position: 'relative',
            width: '96px',
            height: '96px',
            background: v.pfHueBg,
            border: '1px solid var(--gt-ink)',
            /* hard offset shadow, never blurred — the frozen design's own
               literal (template line 702); no token carries .55 */
            boxShadow: '5px 5px 0 rgba(0,0,0,.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--gt-font-pixel)',
            fontSize: '26px',
            color: v.pfInk,
            overflow: 'hidden',
            flex: 'none',
            cursor: 'pointer',
          }}
        >
          <span>{v.pfIni}</span>
          {v.pfHasAv ? <div style={{ position: 'absolute', inset: 0, background: v.pfAvBg }} /> : null}
          {/* DESKTOP-ONLY hover reveal. `d-hov-show` is D0's compiled
              `style-hover="opacity:1"` (§3.11) and beats the inline `opacity:0`
              with `!important`, exactly as the DC runtime did. The overlay
              covers the whole label, so hovering anywhere on the tile reveals
              it. `steps(2)` is a deliberate 2-step stagger, not a smooth fade. */}
          <div
            className="d-hov-show"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '7px',
              background: 'rgba(5,5,8,.82)',
              opacity: 0,
              transition: 'opacity .12s steps(2)',
            }}
          >
            <span style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: '7px', color: 'var(--gt-text)' }}>UPLOAD</span>
            <span style={{ fontFamily: MONO, fontSize: '9px', letterSpacing: '.05em', color: 'var(--gt-text-5)' }}>
              256 × 256 PX
            </span>
          </div>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              e.target.value = '';
              if (f) cropToDataUrl(f, (url) => void s.onPickAv(url));
            }}
            style={{ display: 'none' }}
          />
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '9px', flex: 1, minWidth: '220px' }}>
          <div style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: '15px', color: 'var(--gt-text)' }}>
            <span>{v.pfName}</span>
          </div>
          <div style={{ fontFamily: MONO, fontSize: '10.5px', letterSpacing: '.04em', color: 'var(--gt-muted)' }}>
            <span>{v.myStatsTxt}</span>
          </div>
        </div>
      </div>

      <CharacterPanel desktop />

      {/* IDENTITY beside SECURITY (§1.4 item 4) */}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'stretch' }}>
        <div style={PANEL_HALF}>
          <div style={PANEL_HEAD}>
            <div style={PANEL_TITLE}>IDENTITY</div>
            <div style={PANEL_CAP}>HOW YOUR PARTY SEES YOU</div>
          </div>
          <div style={PANEL_BODY}>
            <div style={GROUP}>
              <span style={LABEL}>DISPLAY NAME</span>
              <input value={v.pfName} onChange={(e) => s.setProfileForm({ pfName: e.target.value })} style={FIELD} />
            </div>
            <div style={{ flex: 1 }} />
            <PixelButton label="SAVE CHANGES" onClick={() => void s.savePf()} fs={8} pad="12px 14px" fit />
          </div>
        </div>

        <div style={PANEL_HALF}>
          <div style={PANEL_HEAD}>
            <div style={PANEL_TITLE}>SECURITY</div>
            <div style={PANEL_CAP}>EMAIL + PASSWORD</div>
          </div>
          <div style={{ ...PANEL_BODY, flex: 1 }}>
            {v.hasPwNote ? (
              <div style={{ fontFamily: MONO, fontSize: '10px', color: 'var(--acc2)' }}>
                <span>{v.pwNote}</span>
              </div>
            ) : null}
            {/* CHANGE EMAIL — the login address (docs/ACCOUNT-IDENTITY.md).
                DESIGNED, NOT PORTED: neither mirror specifies an email-change
                control. Their only email field is IDENTITY's RECOVERY EMAIL,
                saved by SAVE CHANGES with no gate — while the prototype's own
                login matches against that same value, so the design's copy and
                its logic disagree about what the field IS. Marcus settled it
                (2026-08-19): the email is the login, and changing it needs the
                current password.
                It lives HERE rather than in IDENTITY because this panel is
                already the one whose contents require a password — which is
                also why the caption now reads EMAIL + PASSWORD. */}
            <div style={GROUP}>
              <span style={LABEL}>EMAIL — YOU SIGN IN WITH THIS</span>
              <input
                value={v.emNew}
                onChange={(e) => s.setProfileForm({ emNew: e.target.value })}
                type="email"
                placeholder="YOU@EXAMPLE.COM"
                style={FIELD}
              />
            </div>
            {v.showCur ? (
              <div style={GROUP}>
                <span style={LABEL}>CURRENT PASSWORD — TO CHANGE THE EMAIL</span>
                <input
                  type="password"
                  value={v.emCur}
                  onChange={(e) => s.setProfileForm({ emCur: e.target.value })}
                  style={FIELD}
                />
              </div>
            ) : null}
            <PixelButton
              label={v.emLbl}
              busy={v.emBusy}
              onClick={() => void s.saveEmail()}
              fs={8}
              pad="12px 14px"
              fit
            />

            <div style={{ height: '1px', background: 'var(--gt-border)', margin: '2px 0' }} />

            {v.showCur ? (
              <div style={GROUP}>
                <span style={LABEL}>CURRENT PASSWORD</span>
                <input
                  type="password"
                  value={v.pfCur}
                  onChange={(e) => s.setProfileForm({ pfCur: e.target.value })}
                  style={FIELD}
                />
              </div>
            ) : null}
            <div style={GROUP}>
              <span style={LABEL}>NEW PASSWORD</span>
              <input
                type="password"
                value={v.pfN1}
                onChange={(e) => s.setProfileForm({ pfN1: e.target.value })}
                style={FIELD}
              />
            </div>
            <div style={GROUP}>
              <span style={LABEL}>CONFIRM NEW PASSWORD</span>
              <input
                type="password"
                value={v.pfN2}
                onChange={(e) => s.setProfileForm({ pfN2: e.target.value })}
                style={FIELD}
              />
            </div>
            <div style={{ flex: 1 }} />
            <PixelButton
              label={v.pwLbl}
              busy={v.pwBusy}
              onClick={() => void s.savePw()}
              fs={8}
              pad="12px 14px"
              fit
            />
          </div>
        </div>
      </div>

      <div style={{ ...PANEL, border: '1px solid ' + v.pfSteamBd }}>
        <div style={PANEL_HEAD}>
          <div style={{ ...PANEL_TITLE, color: 'var(--acc)' }}>STEAM ACCOUNT</div>
          <div style={PANEL_CAP}>{PF_STEAM_CAP}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '15px 16px' }}>
          <span style={{ width: '8px', height: '8px', flex: 'none', background: pfSteamDot(s) }} />
          <span style={{ fontFamily: MONO, fontSize: '10.5px', color: 'var(--gt-text-3)', flex: 1, minWidth: '200px' }}>
            {v.pfSteamTxt}
          </span>
          <div
            onClick={s.openPtSync}
            style={{
              border: '1px solid var(--acc)',
              color: 'var(--acc)',
              background: 'var(--accSoft)',
              fontFamily: MONO,
              fontSize: '10.5px',
              padding: '9px 13px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <span>{v.pfSteamBtn}</span>
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          border: '1px dashed var(--gt-border-3)',
          padding: '13px 16px',
        }}
      >
        <div style={{ fontFamily: MONO, fontSize: '9.5px', letterSpacing: '.04em', color: 'var(--gt-muted-2)', flex: 1 }}>
          SESSION — SIGNED IN ON THIS DEVICE
        </div>
        <PixelButton
          label="SIGN OUT ✕"
          onClick={() => void s.signOut()}
          variant="outline"
          tone="acc2"
          fs={8}
          pad="11px 14px"
          nowrap
          shadow={false}
        />
      </div>
    </div>
  );
}
